import { Plus, Settings2, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { fmtDate, fmtMoney } from '../../../shared/format'
import { useCreditSettings, useSaveCreditSettings, type CardScope, type CreditSettings, type RateTier } from '../../credit'
import { toastError } from '../../ui'
import { Footer } from '../AccountForms'
import { Field, FormSection } from '../FormBits'

type RateField =
  | 'purchaseRate'
  | 'lateRate'
  | 'cashAdvanceRate'
  | 'cashAdvanceLateRate'
  | 'kmhRate'
  | 'bsmvRate'
  | 'kkdfRate'
  | 'minThreshold'
  | 'minLowRate'
  | 'minHighRate'

const FIELDS: RateField[] = [
  'purchaseRate',
  'lateRate',
  'cashAdvanceRate',
  'cashAdvanceLateRate',
  'kmhRate',
  'bsmvRate',
  'kkdfRate',
  'minThreshold',
  'minLowRate',
  'minHighRate',
]
// Boş bırakılabilenler: nakit avans gecikme (boşsa genel gecikme), KMH (boşsa nakit avans faizi).
const OPTIONAL: RateField[] = ['cashAdvanceLateRate', 'kmhRate']

const NUM = /^\d+([.,]\d{1,4})?$/
const today = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' })

/**
 * Bireysel ve ticari kart/kredi/KMH oranları (finance:credit:settings). Her kayıt geçerlilik tarihli yeni bir sürümdür:
 * ekstre ve tahakkuklar kesim günündeki sürümle hesaplanır, kesilmiş ekstreler değişmez.
 */
export function CreditSettingsModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const q = useCreditSettings()
  const [scope, setScope] = useState<CardScope>('company')
  const current = q.data?.find((s) => s.scope === scope)
  return (
    <Modal
      open
      size="2xl"
      onOpenChange={(v) => !v && onClose()}
      icon={<Settings2 />}
      title={t('finance.credit.settingsTitle')}
      description={t('finance.credit.settingsHint')}
    >
      <Tabs value={scope} onValueChange={(v) => setScope(v as CardScope)}>
        <TabsList size="sm" className="mb-4">
          <TabsTrigger value="company">{t('finance.credit.scope.company')}</TabsTrigger>
          <TabsTrigger value="personal">{t('finance.credit.scope.personal')}</TabsTrigger>
        </TabsList>
      </Tabs>
      {!q.data ? (
        <Skeleton className="h-64" />
      ) : (
        <div className="space-y-6">
          <SettingsForm key={scope} initial={current} scope={scope} onDone={onClose} />
          {current?.history.length ? <History items={current.history} active={current.validFrom} /> : null}
        </div>
      )}
    </Modal>
  )
}

const blank = (scope: CardScope): CreditSettings => ({
  scope,
  validFrom: today(),
  bsmvRate: '',
  kkdfRate: '',
  minThreshold: '',
  minLowRate: '',
  minHighRate: '',
  purchaseRate: '',
  cashAdvanceRate: '',
  lateRate: '',
  cashAdvanceLateRate: '',
  kmhRate: '',
  tiers: [],
  note: '',
  updatedAt: '',
  history: [],
})

function SettingsForm({ initial, scope, onDone }: { initial?: CreditSettings; scope: CardScope; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveCreditSettings()
  // Yeni sürüm: geçerli oranlardan başlar, geçerlilik bugünden.
  const [v, setV] = useState<CreditSettings>(() => (initial ? { ...initial, validFrom: today(), note: '' } : blank(scope)))
  const setTier = (i: number, patch: Partial<RateTier>) =>
    setV((x) => ({ ...x, tiers: x.tiers.map((r, j) => (j === i ? { ...r, ...patch } : r)) }))
  const tiersInvalid = v.tiers.some(
    (r, i) =>
      !NUM.test(r.purchase.trim()) ||
      !NUM.test(r.late.trim()) ||
      (i < v.tiers.length - 1 ? !NUM.test(r.upTo.trim()) : r.upTo.trim() !== ''),
  )
  const invalid =
    !v.validFrom ||
    FIELDS.some((f) => {
      const x = v[f].trim()
      return OPTIONAL.includes(f) ? x !== '' && !NUM.test(x) : !NUM.test(x)
    }) ||
    tiersInvalid
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (invalid) return
        save.mutate(v, {
          onSuccess: () => {
            toast.success(t('finance.credit.settingsSaved'))
            onDone()
          },
          onError: toastError,
        })
      }}
      className="space-y-4"
    >
      <FormSection>
        <Field id="cs-valid" label={t('finance.credit.settingsFields.validFrom')} hint={t('finance.credit.settingsValidHint')}>
          <DateField id="cs-valid" value={v.validFrom} onChange={(d) => setV((x) => ({ ...x, validFrom: d }))} clearable={false} />
        </Field>
        {FIELDS.map((f) => (
          <Field key={f} id={`cs-${f}`} label={t(`finance.credit.settingsFields.${f}`)}>
            <Input
              id={`cs-${f}`}
              inputMode="decimal"
              value={v[f]}
              onChange={(e) => setV((x) => ({ ...x, [f]: e.target.value }))}
            />
          </Field>
        ))}
      </FormSection>
      <div className="space-y-2 rounded-lg border p-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-sm font-medium">{t('finance.credit.tiers.title')}</p>
            <p className="text-muted-foreground text-xs">{t('finance.credit.tiers.hint')}</p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              // Yeni dilim sona (üst sınırsız) eklenir; önceki son dilime üst sınır girilmesi gerekir.
              setV((x) => ({ ...x, tiers: [...x.tiers, { upTo: '', purchase: x.purchaseRate, late: x.lateRate }] }))
            }
          >
            <Plus className="size-4" />
            {t('finance.credit.tiers.add')}
          </Button>
        </div>
        {v.tiers.length ? (
          <Table>
            <THead>
              <TR>
                <TH>{t('finance.credit.tiers.upTo')}</TH>
                <TH>{t('finance.credit.tiers.purchase')}</TH>
                <TH>{t('finance.credit.tiers.late')}</TH>
                <TH />
              </TR>
            </THead>
            <TBody>
              {v.tiers.map((r, i) => (
                <TR key={i}>
                  <TD>
                    {i === v.tiers.length - 1 ? (
                      <span className="text-muted-foreground text-xs">{t('finance.credit.tiers.above')}</span>
                    ) : (
                      <Input
                        aria-label={t('finance.credit.tiers.upTo')}
                        inputMode="decimal"
                        value={r.upTo}
                        onChange={(e) => setTier(i, { upTo: e.target.value })}
                      />
                    )}
                  </TD>
                  <TD>
                    <Input
                      aria-label={t('finance.credit.tiers.purchase')}
                      inputMode="decimal"
                      value={r.purchase}
                      onChange={(e) => setTier(i, { purchase: e.target.value })}
                    />
                  </TD>
                  <TD>
                    <Input
                      aria-label={t('finance.credit.tiers.late')}
                      inputMode="decimal"
                      value={r.late}
                      onChange={(e) => setTier(i, { late: e.target.value })}
                    />
                  </TD>
                  <TD>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-7"
                      aria-label={t('common.delete')}
                      onClick={() =>
                        setV((x) => {
                          const tiers = x.tiers.filter((_, j) => j !== i)
                          return { ...x, tiers: tiers.map((tr, j) => (j === tiers.length - 1 ? { ...tr, upTo: '' } : tr)) }
                        })
                      }
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        ) : (
          <p className="text-muted-foreground text-xs">{t('finance.credit.tiers.none')}</p>
        )}
      </div>
      <Field id="cs-note" label={t('finance.credit.settingsFields.note')}>
        <Input id="cs-note" value={v.note} maxLength={300} onChange={(e) => setV((x) => ({ ...x, note: e.target.value }))} />
      </Field>
      {invalid ? <p className="text-destructive text-xs">{t('finance.credit.settingsInvalid')}</p> : null}
      {initial?.updatedAt ? (
        <p className="text-muted-foreground text-xs">
          {t('finance.credit.settingsCurrent', { from: fmtDate(initial.validFrom), date: fmtDate(initial.updatedAt) })}
        </p>
      ) : null}
      <div className="flex justify-end gap-2 border-t pt-3">
        <Footer pending={save.isPending} disabled={invalid} onCancel={onDone} />
      </div>
    </form>
  )
}

function tierSummary(s: CreditSettings) {
  if (!s.tiers.length) return `%${s.purchaseRate} / %${s.lateRate}`
  return s.tiers.map((r) => `${r.upTo ? `≤ ${fmtMoney(r.upTo)}` : '∞'}: %${r.purchase} / %${r.late}`).join(' · ')
}

/** Oran sürümleri (en yeni üstte): bugün geçerli olan ve ileri tarihli bekleyenler işaretli. */
function History({ items, active }: { items: CreditSettings[]; active: string }) {
  const { t } = useTranslation()
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{t('finance.credit.history.title')}</p>
      <Table>
        <THead>
          <TR>
            <TH>{t('finance.credit.settingsFields.validFrom')}</TH>
            <TH>{t('finance.credit.history.purchase')}</TH>
            <TH>{t('finance.credit.history.cash')}</TH>
            <TH>{t('finance.credit.history.min')}</TH>
            <TH>{t('finance.credit.history.note')}</TH>
          </TR>
        </THead>
        <TBody>
          {items.map((h) => (
            <TR key={h.validFrom}>
              <TD className="whitespace-nowrap">
                {fmtDate(h.validFrom)}
                {h.validFrom === active ? (
                  <span className="text-primary ml-1 text-xs">• {t('finance.credit.history.active')}</span>
                ) : h.validFrom > active ? (
                  <span className="text-warning ml-1 text-xs">• {t('finance.credit.history.pending')}</span>
                ) : null}
              </TD>
              <TD className="text-xs">{tierSummary(h)}</TD>
              <TD className="text-xs whitespace-nowrap">
                %{h.cashAdvanceRate} · KMH %{h.kmhRate}
              </TD>
              <TD className="text-xs whitespace-nowrap">
                {fmtMoney(h.minThreshold)}: %{h.minLowRate} / %{h.minHighRate}
              </TD>
              <TD className="text-muted-foreground text-xs">{h.note}</TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </div>
  )
}
